import { useKeycloak } from "@react-keycloak/web";
import { sidebarPathsAndIcons } from "constants/sidebar";

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  IConfigType,
  IRenderSettings,
  profileView,
} from "services/input-core/configs/@types";
import { useGetClientConfigSideBar } from "services/input-core/configs/client-config-sidebar";
import { VisibleActivityEnum } from "utils/@types";

interface IClientConfigContext {
  isActivityItemsGeneralVisible: boolean;
  viewActivities: profileView;
  clientConfigSidebar: IConfigType;
  isLoadingClientConfigSidebar: boolean;
  setViewActivities: Dispatch<SetStateAction<profileView>>;
  handleChangeViewActivities: (
    event: React.MouseEvent<HTMLElement>,
    nextView: profileView,
  ) => void;
  featuresConfigsActiveByRole: (
    roles: Array<
      | "buyer"
      | "supplier"
      | "worker"
      | "suppliers - primary contact"
      | "stratesys"
      | "superUser"
      | "reports"
    >,
  ) => IRenderSettings[];
  isItemActive: (itemName: string) => boolean;
  isActiveTabByUserRole: () => boolean;
}

interface IClientConfigProps {
  children: ReactNode;
}

const ClientConfigContext = createContext<IClientConfigContext>(
  {} as IClientConfigContext,
);

export function ClientConfigProvider({ children }: IClientConfigProps) {
  const { keycloak } = useKeycloak();

  const isActivityItemsGeneralVisible = keycloak?.hasRealmRole(
    "Visible General Activity",
  );

  const {
    data: clientConfigSidebar,
    isFetching: isLoadingClientConfigSidebar,
  } = useGetClientConfigSideBar();

  const [viewActivities, setViewActivities] = useState<profileView>(
    isActivityItemsGeneralVisible
      ? clientConfigSidebar?.frontendConfig?.activityItems?.profileView ??
          VisibleActivityEnum.MONTHLY
      : VisibleActivityEnum.MONTHLY,
  );

  useEffect(() => {
    isActivityItemsGeneralVisible &&
      setViewActivities(
        () =>
          clientConfigSidebar?.frontendConfig?.activityItems?.profileView ??
          VisibleActivityEnum.MONTHLY,
      );
  }, [clientConfigSidebar]);

  const handleChangeViewActivities = (
    event: React.MouseEvent<HTMLElement>,
    nextView: profileView,
  ) => {
    if (nextView !== null) setViewActivities(nextView);
  };

  const featuresConfigsActiveByRole = useCallback(
    (
      roles: Array<
        "buyer" | "supplier" | "worker" | "suppliers - primary contact"
      >,
    ) => {
      return clientConfigSidebar?.featuresConfigs
        ?.filter(
          (feature) =>
            feature.active &&
            roles?.some((role) => feature?.roles.includes(role)),
        )
        .flatMap((feature) =>
          roles.flatMap((role) =>
            feature.renderSettings
              .filter((setting) => setting.role === role)
              .map((setting) => ({
                ...setting,
                name: feature?.name,
                path: sidebarPathsAndIcons[role][feature?.name]?.path,
                icon: sidebarPathsAndIcons[role][feature?.name]?.icon,
              })),
          ),
        );
    },
    [clientConfigSidebar],
  );

  const isActiveTabByUserRole = useCallback(() => {
    if (!keycloak?.realmAccess?.roles || !clientConfigSidebar?.featuresConfigs) {
      return false;
    }
  
    const userRoles = keycloak?.resourceAccess?.["uverify"]?.roles;
    
    const compareIfRoleExists = clientConfigSidebar.featuresConfigs.some(
      (feature) => 
        feature.active && 
        feature.roles && 
        userRoles.some(userRole => feature.roles.includes(userRole))
    );
    
    return compareIfRoleExists;
  }, [clientConfigSidebar, keycloak?.realmAccess?.roles]);

  const isItemActive = useCallback(
    (itemName: string) => {
      return (
        clientConfigSidebar?.featuresConfigs?.find((i) => i.name === itemName)
          ?.active || false
      );
    },
    [clientConfigSidebar],
  );

  const value = useMemo(
    () => ({
      isActivityItemsGeneralVisible,
      viewActivities,
      clientConfigSidebar,
      isLoadingClientConfigSidebar,
      setViewActivities,
      handleChangeViewActivities,
      featuresConfigsActiveByRole,
      isItemActive,
      isActiveTabByUserRole
    }),
    [
      isActivityItemsGeneralVisible,
      viewActivities,
      clientConfigSidebar,
      isLoadingClientConfigSidebar,
      setViewActivities,
      handleChangeViewActivities,
      featuresConfigsActiveByRole,
      isItemActive,
      isActiveTabByUserRole
    ],
  );

  return (
    <ClientConfigContext.Provider value={value}>
      {children}
    </ClientConfigContext.Provider>
  );
}

export function useClientConfig(): IClientConfigContext {
  const context = useContext(ClientConfigContext);

  if (!context) {
    throw new Error(
      "useClientConfig must be used within an ClientConfigProvider",
    );
  }

  return context;
}
